import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { supabase } from "../../supabase/supabaseClient";
import { POSITION_OPTIONS } from "../../constants/positions";
import { SOCCER_LEVELS } from "../../constants/soccerLevels";
import { getPlayerProfile, updatePlayerProfile, getUserProfile, updateUserProfile, uploadAvatar } from "../../services/profileService";
import { getExperiences } from "../../services/experienceService";
import { getEducations } from "../../services/educationService";
import { getHighlights } from "../../services/highlightsService";
import { getPostsByUser } from "../../services/postsService";
import { getAcceptedConnectionProfiles } from "../../services/connectionsService";
import ExperienceManager from "../ExperienceManager";
import EducationManager from "../EducationManager";
import HighlightsManager from "../HighlightsManager";
import AvatarEditor from "../AvatarEditor";
import "./Profile.css";
import { VscEdit, VscDeviceCamera } from "react-icons/vsc";

const AVATAR_ALLOWED_TYPES = ["image/jpeg", "image/png", "image/webp"];
const AVATAR_MAX_SIZE_BYTES = 5 * 1024 * 1024;

function Profile() {
    const location = useLocation();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [user, setUser] = useState(null);
    // editingSection: null | 'hero' | 'about' | 'attributes'
    const [editingSection, setEditingSection] = useState(null);
    const [sectionSaving, setSectionSaving] = useState(false);
    const [sectionError, setSectionError] = useState("");
    const [profile, setProfile] = useState(location.state?.profile ?? null);
    const [userProfile, setUserProfile] = useState(null); // user_profiles data
    const [experiences, setExperiences] = useState([]);
    const [educations, setEducations] = useState([]);
    const [highlights, setHighlights] = useState([]);
    const [connections, setConnections] = useState([]);
    const [connectionsLoading, setConnectionsLoading] = useState(false);
    const [isScout, setIsScout] = useState(false);
    const [profileRole, setProfileRole] = useState("");
    const [orgAssociation, setOrgAssociation] = useState(null);
    const [userPosts, setUserPosts] = useState([]);
    const [postsLoading, setPostsLoading] = useState(false);
    const [avatarUrl, setAvatarUrl] = useState(null);
    const [avatarUploading, setAvatarUploading] = useState(false);
    const [pendingAvatarFile, setPendingAvatarFile] = useState(null);
    const fileInputRef = useRef(null);

    // Edit form state
    const [editFirstName, setEditFirstName] = useState("");
    const [editLastName, setEditLastName] = useState("");
    const [editPositions, setEditPositions] = useState([]);
    const [editDOB, setEditDOB] = useState("");
    const [editBio, setEditBio] = useState("");
    const [editCity, setEditCity] = useState("");
    const [editHeight, setEditHeight] = useState("");
    const [editWeight, setEditWeight] = useState("");
    const [editFootedness, setEditFootedness] = useState("");
    const [editSoccerLevel, setEditSoccerLevel] = useState("");
    const [experienceEditMode, setExperienceEditMode] = useState(false);
    const [educationEditMode, setEducationEditMode] = useState(false);

    // City autocomplete state
    const [showCityDropdown, setShowCityDropdown] = useState(false);
    const [searchTimeout, setSearchTimeout] = useState(null);
    const [citySuggestions, setCitySuggestions] = useState([]);
    const [cityLoading, setCityLoading] = useState(false);

    const stripOrgPostMarker = (body) => {
        if (typeof body !== "string") return "";
        return body.replace(/\n\n\[posted_as_org:[^\]]+\]\s*$/i, "").trimEnd();
    };

    useEffect(() => {
        const loadProfile = async () => {
            try {
                const { data: sessionData } = await supabase.auth.getSession();
                const authUser = sessionData?.session?.user ?? null;

                if (!authUser) {
                    setLoading(false);
                    return;
                }

                setUser(authUser);
                const userRole = authUser?.user_metadata?.role;
                setProfileRole(userRole);
                const roleNorm = (userRole || "").toString().toLowerCase();
                const isScoutUser = ["scout", "coach","admin"].includes(roleNorm);
                setIsScout(isScoutUser);

                // Fetch user_profile (for org people)
                const { data: upData, error: upError } = await supabase
                    .from("user_profiles")
                    .select("first_name, last_name, role, city, avatar_url")
                    .eq("user_id", authUser.id)
                    .single();
                if (!upError && upData) {
                    setUserProfile(upData);
                }

                // Load organization association for own profile, same pattern as public profile.
                try {
                    const { data: membership } = await supabase
                        .from("org_memberships")
                        .select("org_id, orgrole")
                        .eq("user_id", authUser.id)
                        .limit(1)
                        .maybeSingle();

                    if (membership?.org_id) {
                        const { data: org } = await supabase
                            .from("org_profiles")
                            .select("id, name, type, city")
                            .eq("id", membership.org_id)
                            .maybeSingle();

                        setOrgAssociation({
                            id: membership.org_id,
                            role: membership.orgrole || "",
                            name: org?.name || "",
                            type: org?.type || "",
                            city: org?.city || "",
                        });
                    } else {
                        setOrgAssociation(null);
                    }
                } catch (orgError) {
                    console.error("Error loading own organization association", orgError);
                    setOrgAssociation(null);
                }

                // Always fetch fresh profile data from DB (don't use navigation state for persistence)
                const { data, error } = isScoutUser
                    ? await getUserProfile(authUser.id)
                    : await getPlayerProfile(authUser.id);

                if (error) {
                    console.error("Error loading profile", error);
                } else if (data) {
                    console.log("Profile data loaded:", data);
                    console.log("Height:", data.height, "Weight:", data.weight, "City:", data.city, "Footedness:", data.footedness);
                    setProfile(data);
                    setAvatarUrl(upData?.avatar_url || data?.avatar_url || null);

                    const { data: expData, error: expError } = await getExperiences(authUser.id);
                    if (!expError && expData) {
                        setExperiences(expData);
                    }
                    const { data: eduData, error: eduError } = await getEducations(authUser.id);
                    if (!eduError && eduData) {
                        setEducations(eduData);
                    }
                    const { data: hlData, error: hlError } = await getHighlights(authUser.id);
                    if (!hlError && hlData) {
                        setHighlights(hlData);
                    }

                    setConnectionsLoading(true);
                    try {
                        const acceptedConnections = await getAcceptedConnectionProfiles();
                        setConnections(acceptedConnections);
                    } catch (connError) {
                        console.error("Error loading accepted connections", connError);
                        setConnections([]);
                    } finally {
                        setConnectionsLoading(false);
                    }

                    // Load user posts for Activity section
                    setPostsLoading(true);
                    const { data: postsData, error: postsError } = await getPostsByUser(authUser.id);
                    if (!postsError && postsData) {
                        const mapped = (postsData || []).map((post) => {
                            const media = Array.isArray(post.post_media)
                                ? [...post.post_media]
                                    .sort((a, b) => (a.position || 0) - (b.position || 0))
                                    .map((item) => ({ type: item.media_type, url: item.url }))
                                : [];
                            return {
                                id: post.id,
                                body: stripOrgPostMarker(post.body || ""),
                                media,
                                created_at: post.created_at
                            };
                        });
                        setUserPosts(mapped);
                    }
                    setPostsLoading(false);

                    // Initialize edit form fields
                    // Prefer freshly fetched user_profiles (upData) when available, else fall back to profile data
                    setEditFirstName(upData?.first_name || data.firstname || "");
                    setEditLastName(upData?.last_name || data.lastname || "");
                    setEditPositions(data.positions || []);
                    setEditDOB(data.DateOfBirth || "");
                    setEditBio(data.bio || "");
                    setEditCity(data.city || "");
                    setEditHeight(data.height || "");
                    setEditWeight(data.weight || "");
                    setEditFootedness(data.footedness || "");
                    setEditSoccerLevel(data.soccer_level || "");
                }
            } finally {
                setLoading(false);
            }
        };

        // Always call loadProfile once on mount to ensure user state is set
        loadProfile();
    }, []);

    const handleAvatarChange = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (!AVATAR_ALLOWED_TYPES.includes(file.type)) {
            setSectionError("Please choose a JPG, PNG, or WebP image.");
            e.target.value = "";
            return;
        }

        if (file.size > AVATAR_MAX_SIZE_BYTES) {
            setSectionError("Image must be 5 MB or smaller.");
            e.target.value = "";
            return;
        }

        setSectionError("");
        setPendingAvatarFile(file);
        e.target.value = "";
    };

    const handleOpenAvatarPicker = () => {
        if (avatarUploading) return;
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
            fileInputRef.current.click();
        }
    };

    const handleAvatarApply = async (croppedFile) => {
        setPendingAvatarFile(null);
        setAvatarUploading(true);
        const { data: avatarData, error: uploadError } = await uploadAvatar(user.id, croppedFile);
        if (uploadError) {
            setSectionError(`Photo error: ${uploadError.message}`);
        } else {
            setAvatarUrl(avatarData.avatar_url);
            window.dispatchEvent(new CustomEvent("avatar-updated", {
                detail: { avatarUrl: avatarData.avatar_url }
            }));
        }
        setAvatarUploading(false);
    };

    // --- Section save handlers ---

    const startEditSection = (section) => {
        setSectionError("");
        if (section === "hero") {
            setEditFirstName(userProfile?.first_name || profile.firstname || "");
            setEditLastName(userProfile?.last_name || profile.lastname || "");
            setEditDOB(profile.DateOfBirth || "");
            setEditCity(userProfile?.city || profile?.city || "");
            setCitySuggestions([]);
        } else if (section === "about") {
            setEditBio(profile.bio || "");
        } else if (section === "attributes") {
            setEditPositions(profile.positions || []);
            setEditHeight(profile.height ? String(profile.height) : "");
            setEditWeight(profile.weight ? String(profile.weight) : "");
            setEditFootedness(profile.footedness || "");
            setEditSoccerLevel(profile.soccer_level || "");
            setCitySuggestions([]);
        }
        setEditingSection(section);
    };

    const cancelEditSection = () => {
        setEditingSection(null);
        setSectionError("");
        setShowCityDropdown(false);
    };

    const handleSaveHero = async () => {
        if (!user) return;
        setSectionSaving(true);
        setSectionError("");
        const normalizedCity = editCity?.trim() || "";
        try {
            if (isScout) {
                const { error } = await updateUserProfile(user.id, {
                    first_name: editFirstName || null,
                    last_name: editLastName || null,
                    city: normalizedCity || null,
                });
                if (error) throw error;
                setUserProfile((prev) => ({ ...prev, first_name: editFirstName, last_name: editLastName, city: normalizedCity }));
            } else {
                const { error: upErr } = await updateUserProfile(user.id, {
                    first_name: editFirstName || null,
                    last_name: editLastName || null,
                    city: normalizedCity || null,
                });
                if (upErr) throw upErr;
                const { error: ppErr } = await updatePlayerProfile(user.id, {
                    DateOfBirth: editDOB || null,
                    city: normalizedCity || null,
                });
                if (ppErr) throw ppErr;
                setUserProfile((prev) => ({ ...prev, first_name: editFirstName, last_name: editLastName, city: normalizedCity }));
                setProfile((prev) => ({ ...prev, DateOfBirth: editDOB, city: normalizedCity }));
            }
            setEditingSection(null);
        } catch (err) {
            setSectionError(err.message);
        } finally {
            setSectionSaving(false);
        }
    };

    const handleSaveAbout = async () => {
        if (!user) return;
        setSectionSaving(true);
        setSectionError("");
        try {
            if (isScout) {
                const { error } = await updateUserProfile(user.id, { bio: editBio || null });
                if (error) throw error;
            } else {
                const { error } = await updatePlayerProfile(user.id, { bio: editBio || null });
                if (error) throw error;
            }
            setProfile((prev) => ({ ...prev, bio: editBio }));
            setEditingSection(null);
        } catch (err) {
            setSectionError(err.message);
        } finally {
            setSectionSaving(false);
        }
    };

    const handleSaveAttributes = async () => {
        if (!user) return;
        setSectionSaving(true);
        setSectionError("");
        try {
            const payload = {
                positions: editPositions.length ? editPositions : null,
                height: editHeight ? parseFloat(editHeight) : null,
                weight: editWeight ? parseFloat(editWeight) : null,
                footedness: editFootedness || null,
                soccer_level: editSoccerLevel || null,
            };
            const { error } = await updatePlayerProfile(user.id, payload);
            if (error) throw error;
            setProfile((prev) => ({ ...prev, ...payload }));
            setEditingSection(null);
        } catch (err) {
            setSectionError(err.message);
        } finally {
            setSectionSaving(false);
        }
    };

    // City autocomplete
    const fetchCities = async (query) => {
        if (query.length < 2) { setCitySuggestions([]); return; }
        setCityLoading(true);
        try {
            const response = await fetch(
                `https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(query)}&count=10&language=en`
            );
            const data = await response.json();
            if (data.results) {
                setCitySuggestions(data.results.map((r) => ({ name: r.name, admin1: r.admin1 || "", country: r.country })));
            } else {
                setCitySuggestions([]);
            }
        } catch {
            setCitySuggestions([]);
        } finally {
            setCityLoading(false);
        }
    };

    const handleCitySearch = (value) => {
        setEditCity(value);
        setShowCityDropdown(true);
        if (searchTimeout) clearTimeout(searchTimeout);
        setSearchTimeout(setTimeout(() => fetchCities(value), 300));
    };

    const getAge = (dob) => {
        if (!dob) return null;
        const birth = new Date(dob);
        if (Number.isNaN(birth.getTime())) return null;
        const today = new Date();
        let age = today.getFullYear() - birth.getFullYear();
        const m = today.getMonth() - birth.getMonth();
        if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) {
            age--;
        }
        return age;
    };

    const formatMonthYear = (value) => {
        if (!value) return "Present";
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return "Present";
        return date.toLocaleDateString("en-US", { month: "short", year: "numeric" });
    };

    return (
        <main className="profile-page">
            {pendingAvatarFile && (
                <AvatarEditor
                    file={pendingAvatarFile}
                    onApply={handleAvatarApply}
                    onCancel={() => setPendingAvatarFile(null)}
                />
            )}
            {loading ? (
                <div className="profile-container">
                    <section className="profile-hero profile-skeleton">
                        <div className="hero-background" />
                        <div className="hero-content">
                            <div className="skeleton-avatar" />
                            <div className="skeleton-hero-text">
                                <div className="skeleton-line skeleton-line--name" />
                                <div className="skeleton-line skeleton-line--subtitle" />
                                <div className="skeleton-line skeleton-line--meta" />
                            </div>
                        </div>
                    </section>
                    <div className="profile-grid">
                        {[1, 2, 3].map((index) => (
                            <section key={index} className="profile-card profile-skeleton-card">
                                <div className="skeleton-line skeleton-line--title" />
                                <div className="skeleton-line skeleton-line--full" />
                                <div className="skeleton-line skeleton-line--full" />
                                <div className="skeleton-line skeleton-line--half" />
                            </section>
                        ))}
                    </div>
                </div>
            ) : !profile ? (
                <div className="profile-empty">
                    <p>No profile information available yet. Complete your profile to get started.</p>
                </div>
            ) : (
                <div className="profile-container">
                    <section className="profile-hero">
                        <div className="hero-content">
                            {(() => {
                                // Use user_profiles for display name if available, fallback to profile data
                                const firstName = userProfile?.first_name || profile.firstname || "";
                                const lastName = userProfile?.last_name || profile.lastname || "";
                                const displayName = (`${firstName} ${lastName}`.trim() || "Your Name");
                                const initial = displayName.charAt(0).toUpperCase() || "U";
                                const age = isScout ? null : getAge(profile.DateOfBirth);
                                const cityFrom = userProfile?.city || profile?.city || null;
                                return (
                                    <>
                                        <div className="avatar-wrapper">
                                            {avatarUrl
                                                ? <img src={avatarUrl} alt={displayName} className="avatar-circle avatar-img" />
                                                : <div className="avatar-circle">{initial}</div>
                                            }
                                            <div className="avatar-upload-overlay" onClick={handleOpenAvatarPicker} title="Change photo">
                                                <VscDeviceCamera size={20} />
                                            </div>
                                            {avatarUploading && <div className="avatar-uploading" />}
                                        </div>
                                        {editingSection === "hero" ? (
                                            <div className="hero-inline-edit">
                                                <div className="field-pair">
                                                    <label className="inline-edit-label">
                                                        First Name
                                                        <input
                                                            className="inline-edit-input"
                                                            type="text"
                                                            value={editFirstName}
                                                            onChange={(e) => setEditFirstName(e.target.value)}
                                                        />
                                                    </label>
                                                    <label className="inline-edit-label">
                                                        Last Name
                                                        <input
                                                            className="inline-edit-input"
                                                            type="text"
                                                            value={editLastName}
                                                            onChange={(e) => setEditLastName(e.target.value)}
                                                        />
                                                    </label>
                                                </div>
                                                {!isScout && (
                                                    <label className="inline-edit-label">
                                                        Date of Birth
                                                        <input
                                                            className="inline-edit-input"
                                                            type="date"
                                                            value={editDOB}
                                                            onChange={(e) => setEditDOB(e.target.value)}
                                                            min="1900-01-01"
                                                            max={new Date().toISOString().split("T")[0]}
                                                        />
                                                    </label>
                                                )}
                                                <label className="inline-edit-label">
                                                    City
                                                    <div className="city-autocomplete-wrapper">
                                                        <input
                                                            className="inline-edit-input"
                                                            type="text"
                                                            value={editCity}
                                                            onChange={(e) => handleCitySearch(e.target.value)}
                                                            onFocus={() => setShowCityDropdown(true)}
                                                            onBlur={() => setTimeout(() => setShowCityDropdown(false), 200)}
                                                            placeholder="Search for a city..."
                                                        />
                                                        {showCityDropdown && (
                                                            <div className="city-dropdown">
                                                                {cityLoading && <div className="city-dropdown-item" style={{ cursor: "default", pointerEvents: "none" }}>Searching...</div>}
                                                                {!cityLoading && citySuggestions.length > 0 && citySuggestions.map((s, idx) => (
                                                                    <button
                                                                        type="button"
                                                                        key={idx}
                                                                        className="city-dropdown-item"
                                                                        onClick={() => { setEditCity(s.admin1 ? `${s.name}, ${s.admin1}` : s.name); setShowCityDropdown(false); }}
                                                                    >
                                                                        <div>{s.name}</div>
                                                                        {s.admin1 && <div style={{ fontSize: "0.8rem", color: "rgba(255,255,255,0.6)" }}>{s.admin1}</div>}
                                                                    </button>
                                                                ))}
                                                                {!cityLoading && editCity.length > 1 && citySuggestions.length === 0 && (
                                                                    <div className="city-dropdown-item" style={{ cursor: "default", pointerEvents: "none", color: "rgba(255,255,255,0.5)" }}>No cities found</div>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                </label>
                                                {sectionError && <p className="section-error">{sectionError}</p>}
                                                <div className="form-actions">
                                                    <button className="btn-primary-save" onClick={handleSaveHero} disabled={sectionSaving}>
                                                        {sectionSaving ? "Saving..." : "Save"}
                                                    </button>
                                                    <button className="btn-secondary" onClick={cancelEditSection} disabled={sectionSaving}>
                                                        Cancel
                                                    </button>
                                                </div>
                                            </div>
                                        ) : (
                                            <>
                                                <div className="hero-text">
                                                    <h1 className="profile-name">
                                                        {displayName}
                                                        {profile.verified && <span className="verified-badge">✓</span>}
                                                    </h1>
                                                    <p className="profile-subtitle">{(() => {
                                                        const roleFromUserProfile = userProfile?.role || null;
                                                        const sourceRole = roleFromUserProfile || profileRole || (isScout ? "member" : null);
                                                        return sourceRole ? (sourceRole.charAt(0).toUpperCase() + sourceRole.slice(1)) : "Pre-Alpha Dribbl User";
                                                    })()}</p>
                                                    <div className="hero-meta">
                                                        {Number.isFinite(age) && (
                                                            <span className="meta-pill">Age: {age}</span>
                                                        )}
                                                        {orgAssociation?.name && (
                                                            <Link to={`/org/${orgAssociation.id}`} className="meta-pill meta-pill-link">Org: {orgAssociation.name}</Link>
                                                        )}
                                                        {!isScout && (
                                                            <div className="meta-tags">
                                                                {(profile.positions || []).length ? (
                                                                    (profile.positions || []).map((pos) => (
                                                                        <span key={pos} className="meta-tag">{pos}</span>
                                                                    ))
                                                                ) : (
                                                                    <span className="muted">No positions listed yet</span>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                    {cityFrom && <p className="hero-city">{cityFrom}</p>}
                                                    <Link to="/connections" className="hero-connections-link">
                                                        {connectionsLoading
                                                            ? "loading connections..."
                                                            : `${connections.length} connection${connections.length === 1 ? "" : "s"}`}
                                                    </Link>
                                                </div>
                                                <div className="hero-actions">
                                                    <button className="btn-icon-edit" onClick={() => startEditSection("hero")} title="Edit profile info">
                                                        <VscEdit size={18} />
                                                    </button>
                                                </div>
                                            </>
                                        )}
                                    </>
                                );
                            })()}
                        </div>
                    </section>
                    <input
                        ref={fileInputRef}
                        className="avatar-file-input"
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        onChange={handleAvatarChange}
                    />

                    <div className="profile-grid">
                        <section className="profile-card">
                            <div className="card-header">
                                <h2>About</h2>
                                {editingSection !== "about" && (
                                    <button className="btn-icon-edit" onClick={() => startEditSection("about")} title="Edit about">
                                        <VscEdit size={16} />
                                    </button>
                                )}
                            </div>
                            {editingSection === "about" ? (
                                <div>
                                    <textarea
                                        className="section-textarea"
                                        value={editBio}
                                        onChange={(e) => setEditBio(e.target.value)}
                                        placeholder="Tell scouts about yourself, your achievements, and playing style..."
                                        maxLength={500}
                                        rows={4}
                                    />
                                    <p className="char-count">{editBio.length}/500</p>
                                    {sectionError && <p className="section-error">{sectionError}</p>}
                                    <div className="form-actions">
                                        <button className="btn-primary-save" onClick={handleSaveAbout} disabled={sectionSaving}>
                                            {sectionSaving ? "Saving..." : "Save"}
                                        </button>
                                        <button className="btn-secondary" onClick={cancelEditSection} disabled={sectionSaving}>
                                            Cancel
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <p className="about-text">{profile.bio || <span className="placeholder-text">Add a short bio to tell others about yourself.</span>}</p>
                            )}
                        </section>
                            {!isScout && (
                                <section className="profile-card">
                                    <HighlightsManager
                                        userId={user?.id}
                                        highlights={highlights}
                                        onHighlightsChange={setHighlights}
                                    />
                                </section>
                            )}

                            {!isScout && (
                                <section className="profile-card">
                                    <div className="card-header">
                                        <h2>Attributes</h2>
                                        {editingSection !== "attributes" && (
                                            <button className="btn-icon-edit" onClick={() => startEditSection("attributes")} title="Edit attributes">
                                                <VscEdit size={16} />
                                            </button>
                                        )}
                                    </div>
                                    {editingSection === "attributes" ? (
                                        <div className="attributes-edit-form">
                                            <div className="form-section">
                                                <h3>Playing Positions</h3>
                                                <div className="positions-grid-inner">
                                                    {POSITION_OPTIONS.map(({ value, short }) => (
                                                        <button
                                                            type="button"
                                                            key={value}
                                                            className={`position-pill ${editPositions.includes(value) ? "position-pill-selected" : ""}`}
                                                            onClick={() => {
                                                                setEditPositions((prev) =>
                                                                    prev.includes(value)
                                                                        ? prev.filter((p) => p !== value)
                                                                        : prev.length >= 3 ? prev : [...prev, value]
                                                                );
                                                            }}
                                                        >
                                                            <span className="position-short">{short}</span>
                                                        </button>
                                                    ))}
                                                </div>
                                                <p className="positions-summary">
                                                    {editPositions.length ? `Selected: ${editPositions.join(", ")}` : "No positions selected yet"}
                                                </p>
                                            </div>
                                            <div className="form-section">
                                                <h3>Physical</h3>
                                                <div className="field-pair">
                                                    <label className="inline-edit-label">
                                                        Height (cm)
                                                        <input
                                                            className="inline-edit-input"
                                                            type="number"
                                                            value={editHeight}
                                                            onChange={(e) => setEditHeight(e.target.value)}
                                                            placeholder="e.g., 180"
                                                            min="100"
                                                            max="250"
                                                        />
                                                    </label>
                                                    <label className="inline-edit-label">
                                                        Weight (kg)
                                                        <input
                                                            className="inline-edit-input"
                                                            type="number"
                                                            value={editWeight}
                                                            onChange={(e) => setEditWeight(e.target.value)}
                                                            placeholder="e.g., 75"
                                                            min="30"
                                                            max="200"
                                                        />
                                                    </label>
                                                </div>
                                                <div>
                                                    <label className="inline-edit-label">Dominant Foot</label>
                                                    <div className="button-group" style={{ maxWidth: "none" }}>
                                                        {["Left", "Right", "Both"].map((foot) => (
                                                            <button
                                                                type="button"
                                                                key={foot}
                                                                className={`btn-option ${editFootedness === foot ? "btn-option-selected" : ""}`}
                                                                onClick={() => setEditFootedness(foot)}
                                                            >
                                                                {foot}
                                                            </button>
                                                        ))}
                                                    </div>
                                                </div>
                                                <div>
                                                    <label className="inline-edit-label">
                                                        Soccer Level
                                                        <select
                                                            className="inline-edit-input"
                                                            value={editSoccerLevel}
                                                            onChange={(e) => setEditSoccerLevel(e.target.value)}
                                                        >
                                                            <option value="">Select a level...</option>
                                                            {SOCCER_LEVELS.map((level) => (
                                                                <option key={level} value={level}>{level}</option>
                                                            ))}
                                                        </select>
                                                    </label>
                                                </div>
                                            </div>
                                            {sectionError && <p className="section-error">{sectionError}</p>}
                                            <div className="form-actions">
                                                <button className="btn-primary-save" onClick={handleSaveAttributes} disabled={sectionSaving}>
                                                    {sectionSaving ? "Saving..." : "Save"}
                                                </button>
                                                <button className="btn-secondary" onClick={cancelEditSection} disabled={sectionSaving}>
                                                    Cancel
                                                </button>
                                            </div>
                                        </div>
                                    ) : (profile.height || profile.weight || profile.footedness || profile.soccer_level || (profile.positions || []).length) ? (
                                        <div className="attributes-grid">
                                            {(profile.positions || []).length > 0 && (
                                                <div className="attribute-item attribute-item--wide">
                                                    <span className="attr-label">Positions</span>
                                                    <div className="positions-tags">
                                                        {(profile.positions || []).map((pos) => (
                                                            <span key={pos} className="position-tag">{pos}</span>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                            {profile.height && (
                                                <div className="attribute-item">
                                                    <span className="attr-label">Height</span>
                                                    <span className="attr-value">{profile.height} cm</span>
                                                </div>
                                            )}
                                            {profile.weight && (
                                                <div className="attribute-item">
                                                    <span className="attr-label">Weight</span>
                                                    <span className="attr-value">{profile.weight} kg</span>
                                                </div>
                                            )}
                                            {profile.footedness && (
                                                <div className="attribute-item">
                                                    <span className="attr-label">Dominant Foot</span>
                                                    <span className="attr-value">{profile.footedness}</span>
                                                </div>
                                            )}
                                            {profile.soccer_level && (
                                                <div className="attribute-item">
                                                    <span className="attr-label">Soccer Level</span>
                                                    <span className="attr-value">{profile.soccer_level}</span>
                                                </div>
                                            )}
                                        </div>
                                    ) : (
                                        <p className="placeholder-text">Add your physical attributes and playing level.</p>
                                    )}
                                </section>
                            )}

                        {!isScout && (
                            <section className="profile-card">
                                <div className="card-header">
                                    <h2>Experience</h2>
                                    {!experienceEditMode && (
                                        <button
                                            className="btn-icon-edit"
                                            onClick={() => setExperienceEditMode(true)}
                                            title="Edit experience"
                                        >
                                            <VscEdit size={16} />
                                        </button>
                                    )}
                                </div>
                                {experienceEditMode ? (
                                    <>
                                        <ExperienceManager
                                            userId={user?.id}
                                            experiences={experiences}
                                            onExperiencesChange={setExperiences}
                                        />
                                        <div className="form-actions">
                                            <button
                                                type="button"
                                                className="btn-secondary"
                                                onClick={() => setExperienceEditMode(false)}
                                            >
                                                Done
                                            </button>
                                        </div>
                                    </>
                                ) : experiences.length > 0 ? (
                                    <div className="timeline">
                                        {experiences.map((exp, index) => (
                                            <div key={exp.id} className="timeline-item">
                                                <div className="timeline-dot" />
                                                {index < experiences.length - 1 && <div className="timeline-line" />}
                                                <div className="timeline-content">
                                                    <h3 className="timeline-title">{exp.team_name}</h3>
                                                    <p className="timeline-date">
                                                        {formatMonthYear(exp.start_date)} - {formatMonthYear(exp.end_date)}
                                                    </p>
                                                    {exp.position && <p className="timeline-position">{exp.position}</p>}
                                                    {exp.description && <p className="timeline-description">{exp.description}</p>}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="placeholder-text">No experience yet. Click the edit pencil to add your first role.</p>
                                )}
                            </section>
                        )}

                        {!isScout && (
                            <section className="profile-card">
                                <div className="card-header">
                                    <h2>Education</h2>
                                    {!educationEditMode && (
                                        <button
                                            className="btn-icon-edit"
                                            onClick={() => setEducationEditMode(true)}
                                            title="Edit education"
                                        >
                                            <VscEdit size={16} />
                                        </button>
                                    )}
                                </div>
                                {educationEditMode ? (
                                    <>
                                        <EducationManager
                                            userId={user?.id}
                                            educations={educations}
                                            onEducationsChange={setEducations}
                                        />
                                        <div className="form-actions">
                                            <button
                                                type="button"
                                                className="btn-secondary"
                                                onClick={() => setEducationEditMode(false)}
                                            >
                                                Done
                                            </button>
                                        </div>
                                    </>
                                ) : educations.length > 0 ? (
                                    <div className="timeline">
                                        {educations.map((edu, index) => (
                                            <div key={edu.id} className="timeline-item">
                                                <div className="timeline-dot" />
                                                {index < educations.length - 1 && <div className="timeline-line" />}
                                                <div className="timeline-content">
                                                    <h3 className="timeline-title">{edu.school_name}</h3>
                                                    {(edu.degree || edu.field_of_study) && (
                                                        <p className="timeline-position">
                                                            {edu.degree && edu.field_of_study && `${edu.degree} in ${edu.field_of_study}`}
                                                            {edu.degree && !edu.field_of_study && edu.degree}
                                                            {!edu.degree && edu.field_of_study && edu.field_of_study}
                                                        </p>
                                                    )}
                                                    <p className="timeline-date">
                                                        {formatMonthYear(edu.start_date)} - {formatMonthYear(edu.end_date)}
                                                    </p>
                                                    {edu.description && <p className="timeline-description">{edu.description}</p>}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="placeholder-text">No education yet. Click the edit pencil to add your school history.</p>
                                )}
                            </section>
                        )}

                        <section className="profile-card profile-activity-section">
                            <h2>Activity</h2>
                            {postsLoading ? (
                                <div className="activity-showcase">
                                    <div className="activity-scroll">
                                        {[1, 2, 3].map((index) => (
                                            <div key={index} className="activity-post-card activity-skeleton-card">
                                                <div className="activity-skeleton-media" />
                                                <div className="activity-post-body">
                                                    <div className="activity-skeleton-line activity-skeleton-line--full" />
                                                    <div className="activity-skeleton-line activity-skeleton-line--half" />
                                                    <div className="activity-skeleton-line activity-skeleton-line--date" />
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ) : userPosts.length === 0 ? (
                                <div className="activity-empty">
                                    <p>No posts yet. Share something on the Home feed to see it here.</p>
                                </div>
                            ) : (
                                <div className="activity-showcase">
                                    <div className="activity-scroll">
                                        {userPosts.map((post) => (
                                            <div
                                                key={post.id}
                                                className="activity-post-card"
                                                role="button"
                                                tabIndex={0}
                                                onClick={() => navigate("/home", {
                                                    state: {
                                                        focusPostId: post.id,
                                                        focusPostPreview: {
                                                            id: post.id,
                                                            author: `${userProfile?.first_name || profile.firstname || ""} ${userProfile?.last_name || profile.lastname || ""}`.trim() || "User",
                                                            authorId: user?.id || null,
                                                            authorAvatarUrl: avatarUrl || "",
                                                            body: post.body,
                                                            media: post.media,
                                                            created_at: post.created_at,
                                                        }
                                                    }
                                                })}
                                                onKeyDown={(event) => {
                                                    if (event.key === "Enter" || event.key === " ") {
                                                        navigate("/home", {
                                                            state: {
                                                                focusPostId: post.id,
                                                                focusPostPreview: {
                                                                    id: post.id,
                                                                    author: `${userProfile?.first_name || profile.firstname || ""} ${userProfile?.last_name || profile.lastname || ""}`.trim() || "User",
                                                                    authorId: user?.id || null,
                                                                    authorAvatarUrl: avatarUrl || "",
                                                                    body: post.body,
                                                                    media: post.media,
                                                                    created_at: post.created_at,
                                                                }
                                                            }
                                                        });
                                                    }
                                                }}
                                            >
                                                {post.media.length > 0 && (
                                                    <div className="activity-post-media">
                                                        {post.media[0].type === "video" ? (
                                                            <video src={post.media[0].url} controls preload="metadata" />
                                                        ) : (
                                                            <img src={post.media[0].url} alt="Post media" />
                                                        )}
                                                    </div>
                                                )}
                                                <div className="activity-post-body">
                                                    <p>{post.body.length > 120 ? `${post.body.slice(0, 120)}...` : post.body}</p>
                                                    <div className="activity-post-date">
                                                        {new Date(post.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </section>


                    </div>
                </div>
            )}
        </main>
    );
}

export default Profile;
